/**
 * The ImageLabel facets the Spaces / Styles panel filters on — the same set
 * /rooms exposes, over the same index.
 *
 * These deliberately do NOT go through the shared Rocco filter machinery:
 * `FilterStates.states` is a closed interface and URL writing goes through a
 * fixed `URL_PARAM_MAP`, neither of which knows these keys. Adding them would
 * mean editing the filter core shared by products, professionals, projects and
 * search. Instead each facet is a plain repeated query param, which is what
 * /rooms already does and what the browse loader already reads.
 */
import type { SearchImageAggregateResponse } from '@modules/professional/api/query-search-image.server';

/**
 * Aggregation fields on the searchImage aggregate response. Typing the facet
 * table against these lets the panel read `aggregate[facet.responseKey]`
 * directly instead of casting the response to an index signature.
 */
export type ImageLabelAggregationKey = Extract<
    keyof SearchImageAggregateResponse,
    | 'roomTypePrimary'
    | 'architecturalStyle'
    | 'interiorStyle'
    | 'materialCategory'
    | 'material'
    | 'materialFinish'
    | 'materialColourFamily'
    | 'colorPalettePrimary'
    | 'colorPaletteAccent'
    | 'colorPaletteTone'
    | 'colorPaletteContrast'
    | 'colorPaletteBrightness'
    | 'roomSize'
    | 'ceilingHeight'
    | 'scaleCues'
>;

export interface ImageLabelFacet {
    /** URL search param and searchImage filter key. */
    key: string;
    /** Section heading in the filter panel. */
    label: string;
    /** Aggregation requested from searchImageAggregate. */
    aggregationKey: string;
    /** Key the aggregation comes back under. */
    responseKey: ImageLabelAggregationKey;
    /**
     * False where the searchImage API accepts only a single value for the
     * filter, so the panel offers the options as a radio rather than
     * checkboxes and the loader sends the first value only.
     */
    multi: boolean;
}

export const IMAGE_LABEL_FACETS: ImageLabelFacet[] = [
    {
        key: 'roomTypePrimary',
        label: 'Room or space',
        aggregationKey: 'aggRoomTypePrimary',
        responseKey: 'roomTypePrimary',
        multi: false,
    },
    {
        key: 'architecturalStyle',
        label: 'Architectural style',
        aggregationKey: 'aggArchitecturalStyle',
        responseKey: 'architecturalStyle',
        multi: true,
    },
    {
        key: 'interiorStyle',
        label: 'Interior style',
        aggregationKey: 'aggInteriorStyle',
        responseKey: 'interiorStyle',
        multi: true,
    },
    {
        key: 'materialCategory',
        label: 'Material category',
        aggregationKey: 'aggMaterialCategory',
        responseKey: 'materialCategory',
        multi: true,
    },
    {
        key: 'material',
        label: 'Material',
        aggregationKey: 'aggMaterial',
        responseKey: 'material',
        multi: true,
    },
    {
        key: 'materialFinish',
        label: 'Finish',
        aggregationKey: 'aggMaterialFinish',
        responseKey: 'materialFinish',
        multi: true,
    },
    {
        key: 'materialColourFamily',
        label: 'Material colour',
        aggregationKey: 'aggMaterialColourFamily',
        responseKey: 'materialColourFamily',
        multi: true,
    },
    {
        key: 'colorPalette',
        label: 'Colour palette',
        aggregationKey: 'aggColorPalettePrimary',
        responseKey: 'colorPalettePrimary',
        multi: true,
    },
    {
        key: 'colorPaletteAccent',
        label: 'Accent colour',
        aggregationKey: 'aggColorPaletteAccent',
        responseKey: 'colorPaletteAccent',
        multi: true,
    },
    {
        key: 'colorPaletteTone',
        label: 'Tone',
        aggregationKey: 'aggColorPaletteTone',
        responseKey: 'colorPaletteTone',
        multi: false,
    },
    {
        key: 'colorPaletteContrast',
        label: 'Contrast',
        aggregationKey: 'aggColorPaletteContrast',
        responseKey: 'colorPaletteContrast',
        multi: false,
    },
    {
        key: 'colorPaletteBrightness',
        label: 'Brightness',
        aggregationKey: 'aggColorPaletteBrightness',
        responseKey: 'colorPaletteBrightness',
        multi: false,
    },
    {
        key: 'roomSize',
        label: 'Room size',
        aggregationKey: 'aggRoomSize',
        responseKey: 'roomSize',
        multi: false,
    },
    {
        key: 'ceilingHeight',
        label: 'Ceiling height',
        aggregationKey: 'aggCeilingHeight',
        responseKey: 'ceilingHeight',
        multi: false,
    },
    {
        key: 'scaleCues',
        label: 'Scale cues',
        aggregationKey: 'aggScaleCues',
        responseKey: 'scaleCues',
        multi: true,
    },
];

/** Every facet param name — used to strip filters when clearing. */
export const IMAGE_LABEL_FACET_KEYS = IMAGE_LABEL_FACETS.map(
    (facet) => facet.key,
);

/** One selectable option within a facet, with its result count. */
export interface ImageLabelFacetOption {
    value: string;
    label: string;
    count: number;
    selected: boolean;
}

/** A facet plus its options, as handed to the filter panel. */
export interface ImageLabelFacetGroup {
    key: string;
    label: string;
    multi: boolean;
    options: ImageLabelFacetOption[];
}

/**
 * Read every facet's selected values out of the URL. Values are kept as-is —
 * the index stores raw tokens (`living_room`), and they round-trip unchanged.
 */
export const readFacetSelections = (
    searchParams: URLSearchParams,
): Record<string, string[]> => {
    const selections: Record<string, string[]> = {};
    for (const facet of IMAGE_LABEL_FACETS) {
        const values = searchParams.getAll(facet.key).filter(Boolean);
        if (values.length > 0) {
            selections[facet.key] = values;
        }
    }
    return selections;
};

/**
 * Facet groups as the filter sidebar's leading groups: each row is a link that
 * toggles its own value, so a selection stays shareable. Structural match for
 * `LeadingFilterGroup` — kept as data here rather than importing the UI type,
 * so this config layer stays free of component imports.
 */
export const toFilterGroupLinks = (
    facetGroups: ImageLabelFacetGroup[],
    searchParams: URLSearchParams,
    basePath: string,
    /** Facet keys whose section starts collapsed rather than open. */
    collapsedKeys: string[] = [],
): {
    title: string;
    defaultOpen: boolean;
    singleChoice: boolean;
    items: { id: string; label: string; checked: boolean; to: string }[];
}[] =>
    facetGroups.map((group) => ({
        title: group.label,
        defaultOpen: !collapsedKeys.includes(group.key),
        // The API takes one value for these, so the group can only ever hold
        // one. Checkboxes promised a combination it cannot apply — pick a
        // second room type and the loader silently kept the first.
        singleChoice: !group.multi,
        items: [
            ...(group.multi ? [] : [anyRow(group, searchParams, basePath)]),
            ...group.options.map((option) => ({
                id: `${group.key}:${option.value}`,
                label: `${option.label} (${option.count})`,
                checked: option.selected,
                to: toggleFacetUrl(group, option, searchParams, basePath),
            })),
        ],
    }));

/**
 * The resting row of a single-choice group.
 *
 * A radio ignores a click on the option already chosen, which is what a radio
 * should do — so the group needs a row meaning "no filter" or the sidebar
 * cannot walk a room type back off. The chip bar can also drop the value;
 * this row is the way out that stays inside the group. Same shape the article
 * order group uses for its default.
 */
const anyRow = (
    group: ImageLabelFacetGroup,
    searchParams: URLSearchParams,
    basePath: string,
): { id: string; label: string; checked: boolean; to: string } => {
    const applied = searchParams.getAll(group.key).filter(Boolean);
    const params = new URLSearchParams(searchParams);
    params.delete(group.key);
    params.delete('page');
    const query = params.toString();
    return {
        id: `${group.key}:any`,
        label: 'Any',
        // Read off the URL rather than the options, as `toggleFacetUrl` does.
        // The options come from an aggregation taken with every other facet
        // applied, so a value the reader chose drops out of the list once a
        // sibling facet leaves it with no results — and asking the options
        // would then report "Any" while their filter is still on.
        checked: applied.length === 0,
        to: query ? `${basePath}?${query}` : basePath,
    };
};

/**
 * One chip per applied facet value, labelled without the count, each carrying
 * the URL that drops that value. A checkbox sidebar row uses the same URL; a
 * radio does not — re-clicking the chosen option is a no-op, so the chip (or
 * the group's Any row) is what clears it.
 */
export const toAppliedFacetChips = (
    facetGroups: ImageLabelFacetGroup[],
    searchParams: URLSearchParams,
    basePath: string,
): { id: string; label: string; to: string }[] =>
    facetGroups.flatMap((group) =>
        group.options
            .filter((option) => option.selected)
            .map((option) => ({
                id: `${group.key}:${option.value}`,
                label: option.label,
                to: toggleFacetUrl(group, option, searchParams, basePath),
            })),
    );

/**
 * URL with every facet selection removed. The search term and anything else in
 * the query survive — Clear all clears the refinements, not the reader's own
 * input — and paging restarts for the same reason a single toggle's does.
 */
export const clearAllFacetsUrl = (
    facetGroups: ImageLabelFacetGroup[],
    searchParams: URLSearchParams,
    basePath: string,
): string => {
    const params = new URLSearchParams(searchParams);
    facetGroups.forEach((group) => params.delete(group.key));
    params.delete('page');
    const query = params.toString();
    return query ? `${basePath}?${query}` : basePath;
};

/** URL with one facet value toggled on or off, back at page one. */
const toggleFacetUrl = (
    group: ImageLabelFacetGroup,
    option: ImageLabelFacetOption,
    searchParams: URLSearchParams,
    basePath: string,
): string => {
    const params = new URLSearchParams(searchParams);
    const current = params.getAll(group.key);
    params.delete(group.key);
    if (group.multi) {
        const next = option.selected
            ? current.filter((value) => value !== option.value)
            : [...current, option.value];
        next.forEach((value) => params.append(group.key, value));
    } else if (!option.selected) {
        // Single-value facets replace rather than accumulate.
        params.set(group.key, option.value);
    }
    // A filter change restarts paging; keeping ?page would land the reader in a
    // result set that no longer exists.
    params.delete('page');
    const query = params.toString();
    return query ? `${basePath}?${query}` : basePath;
};
